import { FC, useEffect, useState } from 'react';
import axios from 'axios';
import moment from 'moment';
import { useLocation } from 'react-router-dom';

import { ReactComponent as EmptySlotCalendar } from 'assets/icons/empty-slot-calendar.svg';
import DentalButton from 'components/Buttons/DentalButton/DentalButton';
import { useAppDispatch, useAppSelector } from 'redux/reduxHooks';
import { getSuitableServiceAPI } from 'services/APIs';
import { getClinicNextAvailabilityAPI } from 'services/APIs/getNextAvailability';
import { parseQueryUrl } from 'utils/parseQueryUrl';
import { loadOtpToken } from 'utils/storage';

import AvailabilityProgress from '../AvailabilityProgress/AvailabilityProgress';
import {
  DATE_FORMAT,
  DISPLAY_DATE_FORMAT,
  NEXT_AVAILABILITY_REQUEST_DELAY_MS,
  NEXT_AVAILABILITY_SEARCH_MONTHS,
  NO_AVAILABILITY_IN_RANGE_MESSAGE,
  SEARCH_AGAIN_BUTTON_LABEL,
  SEARCHING_AVAILABILITY_MESSAGE,
  SHORT_DISPLAY_DATE_FORMAT,
  VIEW_NEXT_AVAILABILITY_LABEL_PREFIX,
} from '../constants';
import { INextAvailabilityWindow } from '../types';
import {
  buildSearchWindow,
  getSearchEndDate,
  isExamAndCleaningService,
} from '../utils/availability';
import {
  expireFamilyBookingSession,
  isFamilyBookingSessionExpired,
} from '../../utils/familyBookingSession';

import styles from './NextAvailability.module.scss';

interface NextAvailabilityProps {
  selectedDate: string;
  canAutoNavigate: boolean;
  onDateUpdate: (date: string) => void;
}

const formatRangeDate = (date: string) =>
  moment(date, DATE_FORMAT).format(DISPLAY_DATE_FORMAT);

const delay = (milliseconds: number) =>
  new Promise((resolve) => setTimeout(resolve, milliseconds));

const NextAvailability: FC<NextAvailabilityProps> = ({
  selectedDate,
  canAutoNavigate,
  onDateUpdate,
}) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { codeUrl, serviceSlugNameUrl } = parseQueryUrl(location);

  const {
    familyBookingSlice: {
      selectedPatients,
      currentSelectedPatientIndex,
      familyBookingSessionId,
      step1: { phoneNumber },
      step2: { contactPatient },
    },
    clinicDetailsSlice: {
      clinicData: { id: clinicId, services },
    },
    bookingFormSlice: { selectedLastInsurance },
    authSlice: { tempAccessToken },
  } = useAppSelector((state) => state);

  const currentPatient = selectedPatients[currentSelectedPatientIndex];
  const serviceId = currentPatient?.serviceId;
  const patientId = currentPatient?.id;
  const patientDob = currentPatient?.dob;
  const preferredDentistId = currentPatient?.preferredDentistId;
  const insuranceId =
    selectedLastInsurance?.insuranceCarrierId ||
    contactPatient.insurance?.insuranceId;

  const [nextAvailabilityDate, setNextAvailabilityDate] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchCompleted, setSearchCompleted] = useState(false);
  const [searchedUntilDate, setSearchedUntilDate] = useState('');
  const [currentSearchingRange, setCurrentSearchingRange] =
    useState<INextAvailabilityWindow | null>(null);
  const [startMonth, setStartMonth] = useState(0);

  useEffect(() => {
    const source = axios.CancelToken.source();

    // getSuitableServiceAPI and delay() take no cancel token, so each resumption after an await is guarded by hand.
    let isCurrentSearch = true;

    setNextAvailabilityDate('');
    setSearchCompleted(false);
    setSearchedUntilDate('');
    setCurrentSearchingRange(null);

    if (!clinicId || !serviceId) {
      setIsSearching(false);

      return;
    }

    const searchServiceId = serviceId;
    const isExamCleaningService = isExamAndCleaningService({
      serviceId: searchServiceId,
      services,
    });

    // Searching the following months is an explicit user action, so its result is
    // offered as a button rather than jumping the grid out from under them.
    const isAutoNavigationAllowed = canAutoNavigate && startMonth === 0;

    const resolveWindowServiceId = async (windowStartDate: string) => {
      if (!isExamCleaningService) return searchServiceId;

      const { serviceId: suitableServiceId } = await getSuitableServiceAPI(
        {
          clinicId,
          serviceId: searchServiceId,
          appointmentDate: windowStartDate,
          ...(patientId ? { patientId } : { patientDob }),
        },
        {
          otpToken: loadOtpToken(phoneNumber),
          tmpAccessToken: tempAccessToken,
        }
      );

      return suitableServiceId;
    };

    const findNextAvailability = async () => {
      for (let month = 0; month < NEXT_AVAILABILITY_SEARCH_MONTHS; month++) {
        if (!isCurrentSearch) return;

        const window = buildSearchWindow({
          selectedDate,
          monthOffset: startMonth + month,
        });

        setCurrentSearchingRange(window);

        try {
          const windowServiceId = await resolveWindowServiceId(window.from);

          if (!isCurrentSearch) return;

          const { nextAvailabilityDate: foundDate, searchingDateRanges } =
            await getClinicNextAvailabilityAPI({
              clinicId,
              date: window.from,
              serviceId: windowServiceId,
              cancelToken: source.token,
              ...(codeUrl && { code: codeUrl }),
              ...(serviceSlugNameUrl && {
                serviceSlugName: serviceSlugNameUrl,
              }),
              ...(preferredDentistId && { dentistId: preferredDentistId }),
              ...(insuranceId && { insuranceId }),
              ...(familyBookingSessionId && { familyBookingSessionId }),
            });

          if (!isCurrentSearch) return;

          if (foundDate) {
            if (isAutoNavigationAllowed) {
              onDateUpdate(foundDate);

              return;
            }

            setNextAvailabilityDate(foundDate);
            setSearchedUntilDate(
              searchingDateRanges[searchingDateRanges.length - 1] || window.to
            );
            setSearchCompleted(true);

            return;
          }
        } catch (searchFailure) {
          if (axios.isCancel(searchFailure) || !isCurrentSearch) return;

          if (isFamilyBookingSessionExpired(searchFailure, phoneNumber)) {
            expireFamilyBookingSession({ dispatch, phoneNumber });

            return;
          }
        }

        await delay(NEXT_AVAILABILITY_REQUEST_DELAY_MS);
      }

      if (!isCurrentSearch) return;

      setSearchedUntilDate(getSearchEndDate({ selectedDate, startMonth }));
      setSearchCompleted(true);
    };

    const runSearch = async () => {
      setIsSearching(true);
      await findNextAvailability();

      if (isCurrentSearch) setIsSearching(false);
    };

    runSearch();

    return () => {
      isCurrentSearch = false;
      source.cancel();
    };
  }, [
    canAutoNavigate,
    clinicId,
    codeUrl,
    dispatch,
    familyBookingSessionId,
    insuranceId,
    onDateUpdate,
    patientDob,
    patientId,
    phoneNumber,
    preferredDentistId,
    selectedDate,
    serviceId,
    serviceSlugNameUrl,
    services,
    startMonth,
    tempAccessToken,
  ]);

  const goToNextAvailability = () => {
    if (!nextAvailabilityDate) return;

    setNextAvailabilityDate('');
    onDateUpdate(nextAvailabilityDate);
  };

  const searchNextMonths = () =>
    setStartMonth((month) => month + NEXT_AVAILABILITY_SEARCH_MONTHS);

  if (isSearching && !searchCompleted) {
    return (
      <AvailabilityProgress
        message={SEARCHING_AVAILABILITY_MESSAGE}
        detail={
          currentSearchingRange
            ? `${formatRangeDate(
                currentSearchingRange.from
              )} - ${formatRangeDate(currentSearchingRange.to)}`
            : undefined
        }
      />
    );
  }

  if (nextAvailabilityDate) {
    const isSameCurrentYear = moment(nextAvailabilityDate, DATE_FORMAT).isSame(
      moment(),
      'years'
    );

    const nextAvailabilityText = moment(
      nextAvailabilityDate,
      DATE_FORMAT
    ).format(
      isSameCurrentYear ? SHORT_DISPLAY_DATE_FORMAT : DISPLAY_DATE_FORMAT
    );

    return (
      <div className={styles['container']}>
        <EmptySlotCalendar />

        <DentalButton
          variant="outlined"
          className={styles['next-availability-button']}
          onClick={goToNextAvailability}
        >
          {`${VIEW_NEXT_AVAILABILITY_LABEL_PREFIX} ${nextAvailabilityText}`}
        </DentalButton>
      </div>
    );
  }

  const searchedRangeText = searchedUntilDate
    ? ` from ${formatRangeDate(selectedDate)} to ${formatRangeDate(
        searchedUntilDate
      )}`
    : '';

  return (
    <div className={styles['container']}>
      <EmptySlotCalendar />

      <span className={styles['empty-message']}>
        {`${NO_AVAILABILITY_IN_RANGE_MESSAGE}${searchedRangeText}`}
      </span>

      {searchCompleted && searchedUntilDate && (
        <DentalButton
          variant="outlined"
          className={styles['next-availability-button']}
          onClick={searchNextMonths}
        >
          {SEARCH_AGAIN_BUTTON_LABEL}
        </DentalButton>
      )}
    </div>
  );
};

export default NextAvailability;
